import anime from "animejs";

// shared
export interface animationFactoryOptions {
	targets: string | Node | NodeList | Array<string | Node | NodeList>;
	duration: number;
	onBegin?: (anim: object) => void;
	onComplete?: (anim: object) => void;
	height?: string;
}
type animationFactory = (params: animationFactoryOptions) => object;
interface animationSet {
	[type: string]: animationFactory[];
}
export const easeInOut = "cubicBezier(0.420, 0.000, 0.580, 1.000)";
const animationDefaults = {
	autoplay: false
};
const hookWrapper = callback => {
	return anim => {
		if (callback) {
			callback(anim);
		}
	};
};
export function getCssFriendlyHeight(
	height: number,
	unit = "px",
	defaultHeight = "100%"
) {
	return typeof height === "number" ? `${height}${unit}` : defaultHeight;
}

// opacity
export const opacityEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [0, 1],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const opacityLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [1, 0],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// opacity-up
export const opacityUpEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [0, 1],
		translateY: ["1.5rem", "0"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const opacityUpLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [1, 0],
		translateY: ["0", "1.5rem"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// opacity-down
export const opacityDownEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [0, 1],
		translateY: ["-1.5rem", "0"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const opacityDownLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [1, 0],
		translateY: ["0", "-1.5rem"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// opacity-left
export const opacityLeftEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [0, 1],
		translateX: ["-1.5rem", "0"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const opacityLeftLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [1, 0],
		translateX: ["0", "-1.5rem"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// opacity-right
export const opacityRightEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [0, 1],
		translateX: ["1.5rem", "0"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const opacityRightLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [1, 0],
		translateX: ["0", "1.5rem"],
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// opacity-height
export const opacityHeightEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		opacity: [0, 1],
		height: params.height,
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
// NOTE: there's a bug in animejs right now where a previously animated element does not
// animate if the starting value is not specified again
export const opacityHeightLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		opacity: [1, 0],
		height: [params.height, 0],
		duration: params.duration,
		easing: easeInOut,
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// height
export const heightEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		height: params.height,
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const heightLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		height: [params.height, 0],
		duration: params.duration,
		easing: easeInOut,
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// opacity max-height
export const opacityMaxHeightEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		"max-height": [0, params.height || "100%"],
		opacity: [0, 1],
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const opacityMaxHeightLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		"max-height": [params.height || "100%", 0],
		duration: params.duration,
		easing: easeInOut,
		opacity: [1, 0],
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// max-height
export const maxHeightEnter = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		duration: params.duration,
		easing: easeInOut,
		"max-height": [0, params.height || "100%"],
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};
export const maxHeightLeave = (params: animationFactoryOptions) => {
	return anime({
		...animationDefaults,
		targets: params.targets,
		"max-height": [params.height || "100%", 0],
		duration: params.duration,
		easing: easeInOut,
		round: 100,
		begin: hookWrapper(params.onBegin),
		complete: hookWrapper(params.onComplete)
	});
};

// set #1
export const baseTransitionAnimations: animationSet = {
	opacity: [opacityEnter, opacityLeave],
	"opacity-up": [opacityUpEnter, opacityUpLeave],
	"opacity-down": [opacityDownEnter, opacityDownLeave],
	"opacity-left": [opacityLeftEnter, opacityLeftLeave],
	"opacity-right": [opacityRightEnter, opacityRightLeave],
	"opacity-height": [opacityHeightEnter, opacityHeightLeave],
	"opacity-max-height": [opacityMaxHeightEnter, opacityMaxHeightLeave],
	height: [heightEnter, heightLeave],
	"max-height": [maxHeightEnter, maxHeightLeave]
};
